<script lang="ts">
	//UI
	import {
		Card,
		DefaultSwitch,
		LeftRight,
		PhotoIcon2,
		RightIcon,
	} from "@hitit/new_ui";
	import EditIcon from "@hitit/hub/src/assets/Icons/Edit.png";
	import SaveIcon from "@hitit/hub/src/assets/Icons/Save.png";
	//Utils
	import { api, t } from "@hitit/hub/src/stores";
	import { OBJECT_PURPOSE_STORE_REGISTRATION, putS3File } from "@hitit/api";
	import EditSave from "@hitit/mbr/src/v7/component/Common/EditSave.svelte";
	import StoreNameValue from "@hitit/hub/src/v7/components/store/StoreNameValue.svelte";
	import ComponentLoading from "@hitit/hub/src/v7/components/common/ComponentLoading.svelte";
	import DragAndDrop from "@hitit/hub/src/components/DragAndDrop.svelte";
	import ExposureProductModal from "./ExposureProductModal.svelte";
	import MainProductModal from "./MainProductModal.svelte";
	import OptionModal from "./OptionModal.svelte";
	//Image
	import DownloadImage from "@hitit/hub/src/assets/Icons/DownloadIcon.png";
	//Types
	import type { CategoryData, OptionCategoryData } from "@hitit/api/src/types";
	//Props
	export let storeId = "";
	//Constants
	//Variables
	let loading = false;
	let mouseenter = false;
	let logoMouseenter = false;
	let imageaddr = "";
	let imageUrl: string = "";
	let logoUrl: string = "";
	let editmode = false;
	let available = false;
	let orderType = [
		{ type: "포장", checked: false },
		{ type: "매장", checked: false },
	];
	let logoType = "기본 이미지";
	let exposureProductsModal = false;
	let exposureOptionsModal = false;
	let mainProductsModal = false;
	let categories: CategoryData[] = [];
	let optionCategory: OptionCategoryData[] = [];
	$: registrationStyle = imageUrl ? `background-image : url(${imageUrl})` : "";

	//Functions
	function editHandler() {
		editmode = !editmode;
	}

	function modalClose() {
		exposureProductsModal = false;
		exposureOptionsModal = false;
		mainProductsModal = false;
		document.body.style.overflow = "unset";
		document.body.style.touchAction = "unset";
	}

	function selfOrderFunctionChangeHandler(e: Event) {
		const target = e.target as HTMLInputElement;
		available = target.checked ? true : false;
	}

	function orderTypeClickHandler(type: string) {
		orderType = orderType.map((v) =>
			v.type === type ? { ...v, checked: !v.checked } : v
		);
	}

	function logoTypeClickHandler(type: string) {
		logoType = type;
	}

	function changeImage(file: File | Blob | null) {
		if (!file) {
			imageUrl = "";
			return;
		}
		if (imageUrl) URL.revokeObjectURL(imageUrl);
		imageUrl = URL.createObjectURL(file);
	}

	async function imageFileView() {
		try {
			if (imageUrl === "" || editmode) return;

			const reader = new FileReader();
			const imgData = await fetch(imageUrl);
			const blobData = await imgData.blob();
			reader.readAsDataURL(blobData);
			reader.onloadend = () => {
				const base64 = reader.result;

				if (typeof base64 === "string") {
					const newTab = window.open("", "FullSizeImage");
					newTab?.document.write(`<img src='${base64}' style="width : 100%"/>`);
				}
			};
		} catch (error) {
			console.log(error);
		}
	}

	async function uploadImage(e: { detail: File }) {
		//
		try {
			loading = true;
			const fileUploadResponse = await $api.getFileUploadUrl({
				fileName: "registration",
				fileType: "PNG",
				objectPurpose: OBJECT_PURPOSE_STORE_REGISTRATION,
			});

			await putS3File(fileUploadResponse.data.url, e.detail);
			imageaddr = fileUploadResponse.data.id;

			changeImage(e.detail);
			loading = false;
		} catch (error) {
			console.log(error);
		}
	}

	async function getStoreItem(storeId: string) {
		try {
			loading = true;

			const [categoryResponse, optionResponse] = await Promise.all([
				$api.getStoreItems(storeId),
				$api.getStoreOptionCollection(storeId),
			]);
			const { categoryDTOs } = categoryResponse.data;
			const { optionCollectionDTOs } = optionResponse.data;
			categories = categoryDTOs;
			optionCategory = optionCollectionDTOs;
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	//Do
	$: getStoreItem(storeId);
</script>

{#if exposureProductsModal}
	<ExposureProductModal
		modalClose={modalClose}
		categories={categories}
		editmode={editmode}
	/>
{/if}
{#if exposureOptionsModal}
	<OptionModal
		modalClose={modalClose}
		optionCategory={optionCategory}
		editmode={editmode}
	/>
{/if}
{#if mainProductsModal}
	<MainProductModal
		modalClose={modalClose}
		categories={categories}
		editmode={editmode}
	/>
{/if}
<Card noPadding>
	<div class="container" t-id="SelfOrderManage">
		<div class="title-wrapper">
			<p class="title">{$t("mbr.self_order.self_order")}</p>
			<div class="action" on:click={editHandler}>
				<span>{editmode ? $t("global.done") : $t("global.edit")}</span>
				<img
					src={editmode ? SaveIcon : EditIcon}
					alt={editmode ? "save" : "edit"}
				/>
			</div>
		</div>
		<div class="content">
			<div class="top-section">
				<div class="name-value">
					<StoreNameValue
						name={$t("mbr.self_order.self_order_function")}
						editmode={editmode}
						value={available
							? $t("hub.store.widget.cashreceipt.enabled")
							: $t("hub.store.widget.cashreceipt.disabled")}
					>
						<div slot="input" class="lock-box">
							<DefaultSwitch
								checked={available}
								on:change={selfOrderFunctionChangeHandler}
							/>
						</div>
					</StoreNameValue>
				</div>
				{#if available}
					<div class="name-value">
						<StoreNameValue
							name={$t("mbr.self_order.support_order_format")}
							editmode={editmode}
							value={available
								? $t("hub.store.widget.cashreceipt.enabled")
								: $t("hub.store.widget.cashreceipt.disabled")}
						>
							<div slot="input" class="box">
								{#each orderType as order}
									<button
										class="button"
										class:selected={order.checked}
										on:click={() => orderTypeClickHandler(order.type)}
									>
										{order.type}
									</button>
								{/each}
							</div>
						</StoreNameValue>
					</div>
				{/if}
			</div>
			{#if available}
				<div class="middle-section">
					<div class="middle-box-left">
						<span>{$t("mbr.self_order.representative_image")}</span>
						<div
							class="registration"
							style={registrationStyle}
							on:click={imageFileView}
						>
							{#if editmode}
								<div
									class="drop-cover"
									on:dragover={() => {
										mouseenter = true;
									}}
									on:dragleave={() => {
										mouseenter = false;
									}}
									on:mouseleave={() => {
										mouseenter = false;
									}}
									on:mouseenter={() => {
										mouseenter = true;
									}}
								>
									{#if loading}
										<ComponentLoading />
									{:else}
										<DragAndDrop on:file-update={uploadImage}>
											<div class="photoicon">
												<PhotoIcon2 active={mouseenter} />
											</div>
										</DragAndDrop>
									{/if}
								</div>
							{/if}
						</div>
					</div>
					<div class="middle-box-right">
						<span>{$t("mbr.self_order.none_product_image")}</span>
						{#if editmode}
							<div class="box">
								<button
									class="button"
									class:selected={logoType === "기본 이미지"}
									on:click={() => logoTypeClickHandler("기본 이미지")}
								>
									{$t("mbr.self_order.basic_image")}
								</button>
								<button
									class="button"
									class:selected={logoType === "스토어 로고 추가"}
									on:click={() => logoTypeClickHandler("스토어 로고 추가")}
								>
									{$t("mbr.self_order.register_store_logo")}
								</button>
							</div>
						{/if}
						<div
							class="registration"
							style={registrationStyle}
							on:click={imageFileView}
						>
							{#if logoType === "스토어 로고 추가"}
								<div
									class="drop-cover"
									on:dragover={() => {
										logoMouseenter = true;
									}}
									on:dragleave={() => {
										logoMouseenter = false;
									}}
									on:mouseleave={() => {
										logoMouseenter = false;
									}}
									on:mouseenter={() => {
										logoMouseenter = true;
									}}
								>
									{#if loading}
										<ComponentLoading />
									{:else}
										<DragAndDrop on:file-update={uploadImage}>
											<div class="photoicon">
												<PhotoIcon2 active={logoMouseenter} />
											</div>
										</DragAndDrop>
									{/if}
								</div>
							{/if}
						</div>
					</div>
				</div>
				<div class="bottom-section">
					<div
						class="expose-product"
						on:click={() => {
							exposureProductsModal = true;
							document.body.style.overflow = "hidden";
							document.body.style.touchAction = "none";
						}}
					>
						<span
							>{$t("mbr.self_order.exposure_products")}
							{$t("mbr.stores.manage")}</span
						>
						<LeftRight direction={"right"} />
					</div>
					<div
						class="option"
						on:click={() => {
							exposureOptionsModal = true;
							document.body.style.overflow = "hidden";
							document.body.style.touchAction = "none";
						}}
					>
						<span
							>{$t("mbr.self_order.exposure_options")}
							{$t("mbr.stores.manage")}</span
						>
						<LeftRight direction={"right"} />
					</div>
					<div
						class="main-product"
						on:click={() => {
							mainProductsModal = true;
							document.body.style.overflow = "hidden";
							document.body.style.touchAction = "none";
						}}
					>
						<span
							>{$t("mbr.self_order.representative_products")}
							{$t("mbr.stores.manage")}</span
						>
						<LeftRight direction={"right"} />
					</div>
				</div>
				{#if editmode === false}
					<button class="image-download"
						>{$t("mbr.self_order.qr_image_download")}<img
							src={DownloadImage}
							alt="down"
						/></button
					>
					<button class="selforder-preview"
						>{$t("mbr.self_order.preview_my_store")}
						<div><RightIcon hover={false} /></div>
					</button>
				{/if}
			{/if}
		</div>
	</div>
</Card>

<style lang="scss">
	@import "../../../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		gap: 20px;
		width: 100%;
		padding: 20px 10px;

		.content {
			display: flex;
			flex-direction: column;

			.top-section {
				display: flex;
				flex-direction: column;
				gap: 20px;
			}
			.middle-section {
				display: flex;
				flex-direction: column;
				justify-content: space-between;
				gap: 20px;
				width: 100%;
				margin-top: 20px;

				.middle-box-left {
					display: flex;
					flex-direction: column;
					gap: 10px;

					span {
						color: #6d6f76;
						@include appleAdjustTextForSize(16);
					}

					.registration {
						background-size: contain;
						background-repeat: no-repeat;
						background-position: center center;
						width: 250px;
						height: 450px;
						position: relative;
						background-color: #e6e7ed;
						border-radius: 5px;

						.drop-cover {
							background-color: rgba($color: #e6e7ed, $alpha: 0.8);
							width: 100%;
							height: 100%;
							border-radius: 5px;

							&:hover {
								cursor: pointer;
							}
							.photoicon {
								position: absolute;
								top: 50%;
								left: 50%;
								transform: translate(-50%, -50%);
								width: 60.5px;
								height: 61px;
							}
						}
					}
				}

				.middle-box-right {
					display: flex;
					flex-direction: column;
					gap: 10px;

					span {
						color: #6d6f76;
						@include appleAdjustTextForSize(16);
					}

					.registration {
						background-size: contain;
						background-repeat: no-repeat;
						background-position: center center;
						width: 250px;
						height: 250px;
						position: relative;
						background-color: #e6e7ed;
						border-radius: 5px;

						.drop-cover {
							background-color: rgba($color: #46474b, $alpha: 0.8);
							width: 100%;
							height: 100%;
							border-radius: 5px;

							&:hover {
								cursor: pointer;
							}
							.photoicon {
								position: absolute;
								top: 50%;
								left: 50%;
								transform: translate(-50%, -50%);
								width: 60.5px;
								height: 61px;
							}
						}
					}
				}
			}

			.bottom-section {
				display: flex;
				flex-direction: column;
				gap: 20px;
				margin-top: 20px;

				div {
					display: flex;
					justify-content: space-between;
					cursor: pointer;
				}

				span {
					@include appleAdjustTextForSize(16);
					/* font-size: 1.25rem; */
					font-weight: 500;
					color: #46474b;
				}
			}

			.image-download {
				all: unset;
				display: flex;
				align-items: center;
				gap: 5px;
				width: fit-content;
				@include appleAdjustTextForSize(16);
				font-weight: 400;
				color: #3c68d6;
				border-bottom: 1px solid #3c68d6;
				margin-top: 20px;

				img {
					width: 14px;
					height: 14px;
				}
			}

			:global(.selforder-preview svg path) {
				fill: white;
			}

			.selforder-preview {
				all: unset;
				display: flex;
				justify-content: center;
				align-items: center;
				background-color: #6591fa;
				@include appleAdjustTextForSize(16);
				font-weight: 400;
				color: white;
				gap: 8px;
				border-radius: 5px;
				height: 40px;
				margin-top: 20px;

				div {
					width: 18px;
					height: 16px;
				}
			}

			.name-value {
				display: flex;
				flex-direction: column;
				gap: 10px;
				width: 100%;
				max-width: 100%;

				.button {
					width: 100%;
				}
			}
		}

		.title-wrapper {
			display: flex;
			justify-content: space-between;
			width: 100%;
		}

		.title {
			@include appleAdjustTextForSize(20);
			font-weight: 600;
			color: #46474b;
		}

		.action {
			display: flex;
			gap: 10px;
			align-items: center;
			color: #6d6f76;
			font-weight: 500;
			@include appleAdjustTextForSize(20);

			&:hover {
				cursor: pointer;
			}
			img {
				width: 24px;
				height: 24px;
				object-fit: contain;
			}
		}

		.box {
			display: flex;
			gap: 10px;

			.button {
				width: auto;
				height: 40px;
				padding: 0px 20px 0px 20px;
				background-color: inherit;
				border: 1px solid #bcbfc3;
				border-radius: 20px;
				color: #46474b;
				@include appleAdjustTextForSize(16, true);

				&.selected {
					color: white;
					border: none;
					background-color: #6d6f76;
				}
				&:hover {
					cursor: pointer;
				}
			}
		}
	}
	@include DesktopSize {
		.container {
			padding: 20px 30px;

			.content {
				.top-section {
					display: flex;
					flex-direction: row;
					gap: unset;
				}
				.middle-section {
					flex-direction: row;
					gap: unset;
					width: auto;

					.middle-box-left {
						width: 50%;

						span {
							@include appleAdjustTextForSize(16, false);
						}
					}

					.middle-box-right {
						width: 50%;

						.box{

							button {
								&:nth-child(1) {
									min-width: 127px;
								}
								&:nth-child(2) {
									min-width: 166px;
								}
							}
						}

						span {
							@include appleAdjustTextForSize(16, false);
						}
					}
				}

				.bottom-section {
					span {
						@include appleAdjustTextForSize(16, false);
					}
				}

				.image-download {
					@include appleAdjustTextForSize(16, false);
				}

				.selforder-preview {
					@include appleAdjustTextForSize(16, false);
				}

				.name-value {
					.button {
						width: auto;
					}
				}
			}

			.title {
				@include appleAdjustTextForSize(20, false);
			}

			.action {
				@include appleAdjustTextForSize(20, false);
			}

			.box {
				.button {
					@include appleAdjustTextForSize(16, false);
				}
			}
		}
	}
</style>
